import React, { useState, useRef, useEffect } from 'react';
import { Input, Button, Dropdown, Space, Tag, Spin, Menu } from 'antd';
import { SendOutlined, CalendarOutlined, ProjectOutlined } from '@ant-design/icons';
import { getPerformanceCycles, getPlansForAutocomplete, getCurrentPerformanceCycleContext } from '../../api/chatbot/ChatbotApi';

const EnhancedChatInput = ({ 
  value, 
  onChange, 
  onSend, 
  loading, 
  placeholder = "Ask me anything about your performance...",
  colors 
}) => {
  const [showCycleDropdown, setShowCycleDropdown] = useState(false);
  const [showPlanDropdown, setShowPlanDropdown] = useState(false);
  const [cycles, setCycles] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [currentContext, setCurrentContext] = useState(null);
  const [loadingCycles, setLoadingCycles] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load current performance cycle context on mount
  useEffect(() => {
    loadCurrentContext();
  }, []);

  const loadCurrentContext = async () => {
    try {
      const context = await getCurrentPerformanceCycleContext();
      setCurrentContext(context);
    } catch (error) {
      console.error('Error loading current context:', error);
    }
  };

  // Detect @ and # symbols in input
  useEffect(() => {
    const detectSymbols = () => {
      if (!inputRef.current) return;
      
      const cursorPos = inputRef.current.input?.selectionStart || 0;
      const textBeforeCursor = value.substring(0, cursorPos);
      
      // Check for @ symbol (performance cycles)
      const atMatch = textBeforeCursor.match(/@([^@#\s]*)$/);
      if (atMatch) {
        const query = atMatch[1];
        setShowCycleDropdown(true);
        setShowPlanDropdown(false);
        loadPerformanceCycles(query);
        return;
      }

      // Check for # symbol (plans)
      const hashMatch = textBeforeCursor.match(/#([^@#\s]*)$/);
      if (hashMatch) {
        const query = hashMatch[1];
        setShowPlanDropdown(true);
        setShowCycleDropdown(false);
        loadPlans(query);
        return;
      }

      // No symbols detected
      if (showCycleDropdown || showPlanDropdown) {
        setShowCycleDropdown(false);
        setShowPlanDropdown(false);
      }
    };

    detectSymbols();
  }, [value, showCycleDropdown, showPlanDropdown]);

  const loadPerformanceCycles = async (query = '') => {
    setLoadingCycles(true);
    try {
      const cyclesData = await getPerformanceCycles(query);
      setCycles(cyclesData || []);
    } catch (error) {
      console.error('Error loading performance cycles:', error);
      setCycles([]);
    } finally {
      setLoadingCycles(false);
    }
  };

  const loadPlans = async (query = '') => {
    setLoadingPlans(true);
    try {
      const cycleId = selectedCycle?.id || currentContext?.cycle?.id;
      const plansData = await getPlansForAutocomplete(cycleId, query);
      setPlans(plansData || []);
    } catch (error) {
      console.error('Error loading plans:', error);
      setPlans([]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleCycleSelect = (cycle) => {
    const cursorPos = inputRef.current?.input?.selectionStart || 0;
    const textBeforeCursor = value.substring(0, cursorPos);
    const textAfterCursor = value.substring(cursorPos);
    
    // Replace the @query with @cycle-name
    const newTextBefore = textBeforeCursor.replace(/@([^@#\s]*)$/, `@${cycle.name} `);
    const newValue = newTextBefore + textAfterCursor;
    
    onChange({ target: { value: newValue } });
    setSelectedCycle(cycle);
    setShowCycleDropdown(false);
    
    // Focus back to input
    setTimeout(() => {
      inputRef.current?.focus();
      const newCursorPos = newTextBefore.length;
      inputRef.current?.input?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handlePlanSelect = (plan) => {
    const cursorPos = inputRef.current?.input?.selectionStart || 0;
    const textBeforeCursor = value.substring(0, cursorPos);
    const textAfterCursor = value.substring(cursorPos);
    
    // 🔧 FIX: Use plan ID instead of title for accurate plan identification
    // Format: #PLAN_ID:PLAN_TITLE so backend can extract the exact ID
    const newTextBefore = textBeforeCursor.replace(/#([^@#\s]*)$/, `#${plan.id}:${plan.title} `);
    const newValue = newTextBefore + textAfterCursor;
    
    onChange({ target: { value: newValue } });
    setShowPlanDropdown(false);
    
    // Focus back to input
    setTimeout(() => {
      inputRef.current?.focus();
      const newCursorPos = newTextBefore.length;
      inputRef.current?.input?.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleInputChange = (e) => {
    onChange(e);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  // Create menu items for cycles - COMPACT VERSION
  const cycleMenuItems = cycles.map(cycle => ({
    key: cycle.id,
    label: (
      <div style={{ padding: '6px 8px', minWidth: '280px', maxWidth: '350px' }}>
        <div style={{ fontWeight: '500', color: colors.primary, fontSize: '13px' }}>
          <CalendarOutlined style={{ marginRight: '6px', fontSize: '12px' }} />
          {cycle.name}
        </div>
        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
          {new Date(cycle.startingOn).toLocaleDateString()} - {new Date(cycle.endingOn).toLocaleDateString()}
        </div>
      </div>
    ),
    onClick: () => handleCycleSelect(cycle)
  }));

  // Create menu items for plans - COMPACT VERSION
  const planMenuItems = plans.map(plan => ({
    key: plan.id,
    label: (
      <div style={{ padding: '6px 8px', minWidth: '300px', maxWidth: '400px' }}>
        <div style={{ fontWeight: '500', color: colors.primary, fontSize: '13px' }}>
          <ProjectOutlined style={{ marginRight: '6px', fontSize: '12px' }} />
          {plan.title.length > 45 ? plan.title.substring(0, 45) + '...' : plan.title}
        </div>
        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
          Target: {plan.target} | {plan.assignedTo}
        </div>
      </div>
    ),
    onClick: () => handlePlanSelect(plan)
  }));

  return (
    <div style={{ position: 'relative' }}>
      {/* Enhanced Input with Dropdowns */}
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <Dropdown
          open={showCycleDropdown && cycles.length > 0}
          menu={{ items: cycleMenuItems }}
          placement="topLeft"
          trigger={[]}
          overlayStyle={{ 
            maxHeight: '200px', 
            overflow: 'auto',
            position: 'absolute',
            bottom: '45px', // Position right above input
            zIndex: 1050
          }}
          getPopupContainer={() => dropdownRef.current || document.body}
        >
          <Dropdown
            open={showPlanDropdown && plans.length > 0}
            menu={{ items: planMenuItems }}
            placement="topLeft"
            trigger={[]}
            overlayStyle={{ 
              maxHeight: '250px', 
              overflow: 'auto',
              position: 'absolute',
              bottom: '45px', // Position right above input
              zIndex: 1050
            }}
            getPopupContainer={() => dropdownRef.current || document.body}
          >
            <Space.Compact style={{ width: "100%" }}>
              <Input
                ref={inputRef}
                placeholder={placeholder}
                value={value}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={loading}
                style={{ 
                  borderRadius: "20px 0 0 20px",
                  height: "40px",
                  fontSize: "14px",
                  border: "2px solid rgba(241, 90, 36, 0.38)"
                }}
                suffix={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {(loadingCycles || loadingPlans) && <Spin size="small" />}
                    <div style={{ fontSize: '10px', color: '#999' }}>
                      @ cycles | # plans
                    </div>
                  </div>
                }
              />
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={onSend}
                loading={loading}
                disabled={!value.trim()}
                style={{ 
                  borderRadius: "0 20px 20px 0",
                  height: "40px",
                  background: colors.primary,
                  borderColor: colors.primary,
                  border: "2px solid rgba(241, 90, 36, 0.38)"
                }}
              />
            </Space.Compact>
          </Dropdown>
        </Dropdown>
      </div>
    </div>
  );
};

export default EnhancedChatInput;